import { useEffect, useState } from "react";
import { track } from "@/lib/analytics";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { recipePath } from "@/lib/slug";
import { SHORT_LABELS } from "@/lib/diets";

// "Make a vegetarian copy": preview every swap for one diet, then save a private copy to the cook's cookbook.
export default function DietCopyPanel({ recipe, slug, cookbooks, onClose }) {
  const nav = useNavigate();
  const [plan, setPlan] = useState(null);
  const [target, setTarget] = useState(() => (cookbooks.find((c) => c.cookbook_id === recipe.cookbook_id) || cookbooks[0])?.cookbook_id || "");
  const [busy, setBusy] = useState(false);
  const label = SHORT_LABELS[slug] || slug;

  useEffect(() => {
    api.get(`/recipes/${recipe.recipe_id}/diet-copy-preview`, { params: { slug } })
      .then(({ data }) => setPlan(data))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Couldn't prepare the swaps"); onClose(); });
  }, [recipe.recipe_id, slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!plan) {
    return <div className="mt-2 text-xs text-rf-ink-soft" data-testid={`diet-copy-loading-${slug}`}>Working out swaps…</div>;
  }

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/recipes/${recipe.recipe_id}/diet-copy`, { slug, target_cookbook_id: target });
      toast.success(`Saved “${data.title}” (private)`);
      track("diet_copy", { diet: slug, swaps: data.swaps.length, earns_badge: !!data.earns });
      nav(recipePath(data));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create the copy");
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 rounded-xl border border-emerald-700/30 bg-emerald-50/60 p-3 text-xs text-rf-ink" data-testid={`diet-copy-panel-${slug}`}>
      <p className="font-bold mb-1" data-testid={`diet-copy-summary-${slug}`}>
        {plan.swaps.length} swap{plan.swaps.length === 1 ? "" : "s"} ready
        {plan.unswappable.length > 0 ? `; ${plan.unswappable.join(", ")} ${plan.unswappable.length === 1 ? "has" : "have"} no swap` : ""}
        {plan.earns ? ` — the copy will be ${label}.` : ` — the copy won't fully earn ${label} yet.`}
      </p>
      <ul className="list-disc pl-5 mb-2 space-y-0.5">
        {plan.swaps.map((s) => <li key={s.ingredient}>{s.ingredient} → <b>{s.swap}</b>{s.tip ? ` (${s.tip})` : ""}</li>)}
      </ul>
      {cookbooks.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <label className="sr-only" htmlFor={`diet-copy-target-${slug}`}>Save to cookbook</label>
          <select id={`diet-copy-target-${slug}`} value={target} onChange={(e) => setTarget(e.target.value)} data-testid={`diet-copy-target-${slug}`}
            className="rounded-lg border border-rf-ink/20 bg-white px-2 py-1">
            {cookbooks.map((c) => <option key={c.cookbook_id} value={c.cookbook_id}>{c.title}</option>)}
          </select>
          <button type="button" disabled={busy || !plan.swaps.length || !target} onClick={create} data-testid={`diet-copy-create-${slug}`}
            className="rounded-full bg-emerald-700 text-white font-bold px-3 py-1 hover:bg-emerald-800 disabled:opacity-50">
            {busy ? "Creating…" : `Create ${label} copy`}
          </button>
          <button type="button" onClick={onClose} data-testid={`diet-copy-cancel-${slug}`} className="font-bold underline text-rf-ink-soft">Cancel</button>
        </div>
      ) : (
        <p className="text-rf-ink-soft">Create a cookbook first to save copies. <button type="button" onClick={onClose} className="underline font-bold">Close</button></p>
      )}
    </div>
  );
}
